import React, {useEffect, useRef, useState, useMemo, useCallback} from "react";
import FilterIcon from '../FilterIcon';

import './Datatable.scss';

const Datatable = props => {
	const {
		data,
		patchBgColor,
		onRowClick,
		gridHeight,
		preventRowHighlight,
		resetFilters,
		filters,
		onFilterChange,
		sortBy,
		sortOrder,
		filterOrder,
		uniqueValuesByColumn,
		containerClassName
	} = props;

	const columns = props.columns.filter(c => c.visible == null || c.visible === true);

	const headersRef = useRef(null);
	const [colWidths, setColWidths] = useState([]);
	const [userWidths, setUserWidths] = useState({}); // {colIdx: width}
	const tableRef = useRef();
	const resizingCol = useRef(null);
	const startX = useRef(0);
	const startWidth = useRef(0);
	const [containerWidth, setContainerWidth] = useState(0);
	const prevDataLength = useRef(data?.length || 0);

	const columnsKey = useMemo(
		() => columns.map(col => col.id || col.name).join(','),
		[columns]
	);

	// Track container width
	useEffect(() => {
		const updateWidth = () => {
			if (tableRef.current) {
				setContainerWidth(tableRef.current.offsetWidth);
			}
		};
		updateWidth();
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, []);

	// Auto-size columns to content (max 200px), unless user has resized
	useEffect(() => {
		// Always recalculate widths if columns or data change
		setTimeout(() => {
			if (!tableRef.current) return;
			const headerCells = tableRef.current.querySelectorAll('.datagrid-header');
			const rowCells = tableRef.current.querySelectorAll('.datagrid-row');

			let widths = columns.map((col, colIdx) => {
				if (userWidths[colIdx]) return userWidths[colIdx];
				
				// Use column width if defined, otherwise auto-calculate
				if (col.width) {
					return col.width;
				}
				
				let maxWidth = 0;
				if (headerCells[colIdx]) {
					maxWidth = headerCells[colIdx].scrollWidth;
				}
				if (data.length > 0) {
					rowCells.forEach(row => {
						const cell = row.children[colIdx];
						if (cell) {
							maxWidth = Math.max(maxWidth, cell.scrollWidth);
						}
					});
				}
				return Math.min(maxWidth + 4, 300) + 'px';
			});
			// If total width < container, stretch last column
			const totalWidth = widths.reduce((sum, w) => sum + parseInt(w), 0);
			if (containerWidth && totalWidth < containerWidth && widths.length > 0) {
				widths[widths.length - 1] = '1fr';
			}
			if (JSON.stringify(widths) !== JSON.stringify(colWidths)) {
				setColWidths(widths);
			}
		}, 0);
		prevDataLength.current = data?.length || 0;
		// eslint-disable-next-line
	}, [data, columns]);

	// Only reset userWidths when columns actually change (by id or name)
	useEffect(() => {
		setUserWidths({});
	}, [columnsKey]);

	// Drag-to-resize logic - define functions first
	const onMouseMoveResizer = useCallback((e) => {
		if (resizingCol.current !== null) {
			const diff = e.clientX - startX.current;
			const newWidth = Math.max(20, Math.min(600, startWidth.current + diff));
			setUserWidths(widths => ({ ...widths, [resizingCol.current]: `${newWidth}px` }));
		}
	}, []);

	const onMouseUpResizer = useCallback(() => {
		resizingCol.current = null;
		document.removeEventListener('mousemove', onMouseMoveResizer);
		document.removeEventListener('mouseup', onMouseUpResizer);
	}, [onMouseMoveResizer]);

	const onMouseDownResizer = (e, colIdx) => {
		e.stopPropagation();
		resizingCol.current = colIdx;
		startX.current = e.clientX;
		startWidth.current = parseInt(colWidths[colIdx], 10);
		document.addEventListener('mousemove', onMouseMoveResizer);
		document.addEventListener('mouseup', onMouseUpResizer);
	};

	// Cleanup event listeners on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', onMouseMoveResizer);
			document.removeEventListener('mouseup', onMouseUpResizer);
		};
	}, [onMouseMoveResizer, onMouseUpResizer]);

	const renderColumn = (column, index) => {
		let sortIcon = null;

		let stl = {width: colWidths[index] || 'auto', position: 'relative'};

		if(column.headerStyle) {
			stl = {...stl, ...column.headerStyle};
		}

		return (
			<div key={index} className="datagrid-header" style={stl}>
				{column.header ? column.header(column, index) : column.name}
				{sortIcon}
				{/* Only show FilterIcon if hideFilter is not true */}
				{!column.hideFilter && (
					<FilterIcon 
						column={column.id} 
						data={data} 
						allUniqueValues={uniqueValuesByColumn[column.id]}
						onFilterChange={onFilterChange}
						filterResetKey={resetFilters}
						filters={filters}
						sortBy={sortBy}
						sortOrder={sortOrder}
						filterOrderIndex={filterOrder?.indexOf(column.id)}
					/>
				)}
				{/* Resizer handle */}
				<span
					className="datagrid-header-resizer"
					onMouseDown={e => onMouseDownResizer(e, index)}
					style={{position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2}}
				/>
			</div>
		)
	};

	const widths = colWidths;

	const renderCell = (row, column, colIdx) => {
		return (
			<div key={Math.random()} className="datagrid-cell" style={{...(column.cellStyle || {}), width: colWidths[colIdx] || 'auto'}}>{column.cell ? column.cell(row) : row[column.id] ? row[column.id] : '\u00A0'}</div>
		)
	};

	const renderRow = (row, index) => {
		return (
			<div key={index} className={`datagrid-row ${preventRowHighlight ? 'no-highlight' : ''}`} onClick={() => onRowClick ? onRowClick(row, index) : {}} style={{gridTemplateColumns:widths.join(" ")}}>
				{ columns.map((column, colIdx) => renderCell(row, column, colIdx))}
			</div>
		)
	};

	const handleScroll = (e) => {
		headersRef.current.scrollLeft = e.target.scrollLeft;
	};

	const isLayoutReady = useMemo(() => (
		colWidths.length === columns.length &&
		colWidths.every(width => typeof width === 'string' && width.length > 0)
	), [colWidths, columns.length]);

	const containerStyle = useMemo(() => ({
		visibility: isLayoutReady ? 'visible' : 'hidden'
	}), [isLayoutReady]);

	return (
		<div
			className={`datagrid-container ${containerClassName || ''}`}
			ref={tableRef}
			style={containerStyle}
		>
			<div className="datagrid-headers-scroll-patch" style={{backgroundColor: patchBgColor || '#F9F9F7'}} />

			<div ref={headersRef} className="datagrid-headers-container">
				<div className="datagrid-headers" style={{gridTemplateColumns:widths.join(" ")}}>
					{ columns.map((c, i) => renderColumn(c, i) )}
				</div>
			</div>

			<div className="datagrid-body" onScroll={handleScroll} style={{height: gridHeight || '300px'}}>
				{ data.map((row, i) => renderRow(row, i) )}
			</div>
		</div>
	)
};

export default Datatable;
